import { useEffect, useRef } from 'react';
import { MergeView } from '@codemirror/merge';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { markdown } from '@codemirror/lang-markdown';
import { cortexTheme, cortexHighlight } from './theme';

export interface ConflictDialogProps {
  path: string;
  disk: string;
  mine: string;
  onUseDisk: () => void;
  onOverwrite: (merged: string) => void;
  onCancel: () => void;
}

/** Side-by-side merge: disk version on the left (read-only), yours on the right (editable). */
export function ConflictDialog({ path, disk, mine, onUseDisk, onOverwrite, onCancel }: ConflictDialogProps) {
  const host = useRef<HTMLDivElement>(null);
  const mv = useRef<MergeView | null>(null);

  useEffect(() => {
    if (!host.current) return;
    const ext = [cortexTheme, cortexHighlight, markdown(), EditorView.lineWrapping];
    mv.current = new MergeView({
      a: { doc: disk, extensions: [...ext, EditorState.readOnly.of(true)] },
      b: { doc: mine, extensions: ext },
      parent: host.current,
      highlightChanges: true,
      gutter: true,
      collapseUnchanged: { margin: 3, minSize: 4 },
    });
    return () => mv.current?.destroy();
  }, [disk, mine]);

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dialog wide panel ticks glow-orange" role="dialog" aria-label="Conflict">
        <div className="dialog-head">
          <span className="light crit" />
          <span>Changed on disk</span>
          <span className="mono" style={{ color: 'var(--text-faint)' }}>{path}</span>
          <span style={{ marginLeft: 'auto' }} className="merge-legend">
            <span>◀ on disk (read-only)</span>
            <span>yours (editable) ▶</span>
          </span>
        </div>
        <div className="dialog-body">
          <div className="merge-host" ref={host} />
        </div>
        <div className="dialog-foot">
          <button onClick={onCancel}>Keep editing</button>
          <button className="ghost" onClick={onUseDisk}>Take disk version</button>
          <button className="primary" onClick={() => onOverwrite(mv.current?.b.state.doc.toString() ?? mine)}>
            Save right side
          </button>
        </div>
      </div>
    </div>
  );
}
